import { useCallback, useEffect, useRef, useState } from 'react';
import { IconChevron } from '@components/icons';
import { Input } from '@components/input';
import { InputCheckbox } from '@components/input-checkbox';
import { Loader } from '@components/loader';
import { useOnClickOutside } from '@hooks/use-click-outside';
import classNames from 'classnames';

import { Props } from './multiselect.type';

// eslint-disable-next-line max-lines-per-function
export const Multiselect = ({
  id,
  hasError,
  options = [],
  onChange,
  isLoading = false,
  children,
  handleSelectedAll,
  isCheckboxRounded = false,
  strikeThrough,
  translations,
  parentRef,
  readOnly,
  ...props
}: Props) => {
  const itemsEls = useRef<HTMLLIElement[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownList = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [cursor, setCursor] = useState<number>(0);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOnClickOutside(wrapperRef, handleClickOutside);

  const setInputFocused = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const calculateDropdownPosition = useCallback(() => {
    const dropdownListComponent = dropdownList.current;
    if (!dropdownListComponent || !inputRef.current || !parentRef?.current) return;
    const availableSpaceBelow =
      parentRef.current.clientHeight - inputRef.current.getBoundingClientRect().bottom <
      dropdownListComponent.clientHeight;
    return availableSpaceBelow;
  }, [parentRef]);

  const handleClickInside = useCallback(() => {
    setIsOpen(true);
    setCursor(0);
    setInputFocused();
  }, [setInputFocused]);

  const moveToCursor = useCallback(() => {
    if (itemsEls.current[cursor]) {
      itemsEls.current[cursor].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'end',
      });
    }
  }, [cursor]);

  useEffect(() => {
    setCursor(0);
  }, [options]);

  useEffect(() => {
    if (isOpen) {
      moveToCursor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = () => {
    // TODO: handle search
  };

  const selectedOptions = options.filter((option) => !!option.checked);
  const readableValue = selectedOptions.length
    ? `${selectedOptions
        .slice(0, 10)
        .map((selectedOption) => selectedOption.name)
        .join(', ')}`
    : translations.noOptionsSelected;
  const areAllSelected = options.every((option) => option.checked);
  const areAllSelectedLabel = areAllSelected ? translations.deselectAll : translations.selectAll;

  return (
    <div className='mt-1' ref={wrapperRef}>
      <div
        className={`flex gap-x-2 gap-y-1 flex-wrap items-center fill-primary relative w-full rounded-md border text-body-s text-primary placeholder-gray-500 focus-within:outline-hidden focus-within:ring-0 lg:text-body-m group ${classNames(
          {
            'border-critical-no-bg': hasError,
            'border-soft': !hasError,
            'focus-within:border-hard': !hasError,
          }
        )}`}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-labelledby={`${id}-button ${id}`}
        id={`${id}-button`}
        ref={dropdownList}>
        <div className='relative w-full'>
          <div className='absolute inset-y-px right-px pr-3 flex items-center rounded-md bg-neutral-0 dark:bg-neutral-70 group-hover:bg-neutral-2 dark:group-hover:bg-neutral-60 pointer-events-none'>
            <IconChevron className='transform -rotate-90 w-3 h-3 text-primary' aria-hidden={true} />
          </div>
          <Input
            {...props}
            id={id}
            onClick={handleClickInside}
            value={readableValue}
            onChange={handleChange}
            className='w-full truncate flex grow cursor-pointer'
            type='text'
            readOnly
            ref={inputRef}
            isChangingInactiveColor={false}
          />
        </div>
        <div
          className={classNames(
            'absolute left-0 right-0 z-30 mt-1 bg-surface-primary shadow-custom rounded-md py-5 px-3 flex flex-col items-start',
            {
              hidden: !isOpen || (!options.length && !selectedOptions.length),
              'top-14': !calculateDropdownPosition(),
              'bottom-14': calculateDropdownPosition(),
            }
          )}>
          <ul
            id={`${id}-list`}
            tabIndex={-1}
            role='listbox'
            aria-label={id}
            aria-labelledby={id}
            aria-activedescendant={id}
            className='py-2 pl-2 pr-10 flex flex-col space-y-6 h-auto max-h-64 overflow-auto scrollbar w-full'>
            {isLoading && (
              <li className='text-primary select-none relative py-2 px-3 cursor-pointer'>
                <Loader />
              </li>
            )}

            {!isLoading && !options.length && (
              <li className='text-primary select-none relative py-2 px-3 cursor-pointer'>
                <span>{translations.noResultsFound}</span>
              </li>
            )}

            {!isLoading && !!options.length && (
              <>
                <InputCheckbox
                  name={`selectAll`}
                  label={areAllSelectedLabel}
                  value={areAllSelected}
                  rounded={isCheckboxRounded}
                  strikeThrough={strikeThrough}
                  onChange={handleSelectedAll}
                />
                {options.map((option) => (
                  <InputCheckbox
                    key={option.id}
                    name={option.id}
                    label={option.name}
                    value={option.checked}
                    rounded={isCheckboxRounded}
                    strikeThrough={strikeThrough}
                    disabled={option.disabled}
                    onChange={(val) => {
                      onChange(option.id, val);
                    }}
                  />
                ))}
              </>
            )}

            {!isLoading && children ? children : null}
          </ul>
        </div>
      </div>
    </div>
  );
};
